'use client';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useRef, useMemo, useCallback, useEffect } from 'react';
import type { ChartCanvas } from 'react-financial-charts';
import { useStore } from 'zustand';
import { createChartUIStore } from '../stores/chartUIStore';
import type { ChartUIStoreInstance, YCoordinateClickedData } from '../stores/chartUIStore';
import type { ChartInfo } from '../types/commontypes';

export type { YCoordinateClickedData } from '../stores/chartUIStore';

// ─── Context (DOM refs + store instance only) ────────────────────

interface ChartUIContextValue {
  chartCanvasRef: React.RefObject<ChartCanvas<number | Date> | null>;
  chartContainerRef: React.RefObject<HTMLDivElement | null>;
  chartUIStore: ChartUIStoreInstance;
}

const ChartUIContext = createContext<ChartUIContextValue | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────

interface ChartUIProviderProps {
  children: ReactNode;
  initialYCoordinateValue?: YCoordinateClickedData;
  timeframe?: string;
  initialChartInfos?: ChartInfo[];
}

export const ChartUIProvider: React.FC<ChartUIProviderProps> = ({
  children,
  initialYCoordinateValue,
  timeframe,
  initialChartInfos,
}) => {
  // ─── chartUIStore 인스턴스 관리 (useRef + factory) ───
  const storeRef = useRef<ChartUIStoreInstance | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createChartUIStore(timeframe ?? '', initialChartInfos, initialYCoordinateValue);
  }
  const store = storeRef.current;

  // timeframe 변경 시 store를 재생성하지 않고, 새 timeframe의 localStorage 데이터로 리셋
  const prevTimeframeRef = useRef(timeframe);
  useEffect(() => {
    if (prevTimeframeRef.current !== timeframe) {
      // timeframe이 변경되면 기존 store를 destroy하고 새 store 생성
      store.destroy();
      storeRef.current = createChartUIStore(timeframe ?? '', initialChartInfos, initialYCoordinateValue);
      prevTimeframeRef.current = timeframe;
    }
  }, [timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  // cleanup on unmount
  useEffect(() => {
    return () => {
      storeRef.current?.destroy();
    };
  }, []);

  // ─── DOM refs ───
  const chartCanvasRef = useRef<ChartCanvas<number | Date>>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // ─── Window resize: reposition mini toolbar within bounds ───
  const handleResize = useCallback(() => {
    const currentStore = storeRef.current;
    if (!currentStore) return;

    const { miniToolbarBoundsEl, miniToolbarState, setMiniToolbarState } = currentStore.getState();
    if (!miniToolbarBoundsEl) return;

    const rect = miniToolbarBoundsEl.getBoundingClientRect();
    const { x, y, width, height } = miniToolbarState;

    const bounds = {
      top: rect.top,
      left: rect.left,
      right: rect.right - width,
      bottom: rect.bottom - height,
    };

    let newX = x;
    let newY = y;
    let needsUpdate = false;

    if (newX < bounds.left) {
      newX = bounds.left;
      needsUpdate = true;
    } else if (newX > bounds.right) {
      newX = bounds.right;
      needsUpdate = true;
    }

    if (newY < bounds.top) {
      newY = bounds.top;
      needsUpdate = true;
    } else if (newY > bounds.bottom) {
      newY = bounds.bottom;
      needsUpdate = true;
    }

    if (needsUpdate) {
      setMiniToolbarState({ ...miniToolbarState, x: newX, y: newY });
    }
  }, []);

  // Reposition when toolbar size changes
  const miniToolbarWidth = useStore(store, s => s.miniToolbarState.width);
  const miniToolbarHeight = useStore(store, s => s.miniToolbarState.height);
  useEffect(() => {
    handleResize();
  }, [miniToolbarWidth, miniToolbarHeight, handleResize]);

  // Register resize listener
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // ─── Context value (DOM refs + store) ───
  const contextValue: ChartUIContextValue = useMemo(
    () => ({
      chartCanvasRef,
      chartContainerRef,
      chartUIStore: store,
    }),
    [store],
  );

  return <ChartUIContext.Provider value={contextValue}>{children}</ChartUIContext.Provider>;
};

// ─── Hooks ───────────────────────────────────────────────────────

/**
 * Context 내부의 chartUIStore 인스턴스에 직접 접근한다.
 * selector hook이나 getState() 호출에 사용한다.
 */
export const useChartUIContext = () => {
  const context = useContext(ChartUIContext);
  if (!context) {
    throw new Error('useChartUIContext must be used within ChartUIProvider');
  }
  return context;
};
